/**
 * 枚举 → 中文展示名（**薄适配层**）。
 *
 * 中文标签的唯一来源是 `@crossmatch/shared` 的 `labels.ts`：
 * 后端可解释理由（如「自评短板：设计」）、成队后自动生成的阶段待办标题、
 * 前端标签与看板列名三处必须是同一套叫法，所以这里不再各写一份，
 * 只做「重命名到本项目的命名习惯」+「前端专属的顺序表/预填映射」。
 *
 * 注意：本文件只负责展示文案，不参与任何计分。
 */
import type { AbilityDimension, SkillCategory, SkillLevel, Task, TeamMember, TeamRole } from '@crossmatch/shared';

// —— 与领域层/后端共用的标签：直接复用 shared ——
export {
  ABILITY_LABELS,
  abilityLabel,
  CATEGORY_LABELS as SKILL_CATEGORY_LABELS,
  categoryLabel as skillCategoryLabel,
  ROLE_LABELS,
  roleLabel,
  PHASE_LABELS,
  phaseLabel as taskPhaseLabel,
  TEAM_STATUS_LABELS,
  teamStatusLabel,
  TASK_STATUS_LABELS,
  taskStatusLabel,
  MEMBER_STATUS_LABELS,
  memberStatusLabel,
} from '@crossmatch/shared';

export type TeamStatus = 'forming' | 'confirmed' | 'archived';
export type MemberStatus = TeamMember['status'];
export type TaskStatus = Task['status'];
export type TaskPhase = Task['phase'];

// —— 以下是纯前端关注点：展示顺序、界面预填、以及 shared 未覆盖的枚举 ——

/** 技能大类在「技能选择器」里的展示顺序 */
export const SKILL_CATEGORY_ORDER: SkillCategory[] = ['dev', 'design', 'business', 'data', 'content', 'speech', 'domain'];

/** 能力维度在画像页的展示顺序 */
export const ABILITY_ORDER: AbilityDimension[] = ['tech', 'design', 'business', 'data', 'content', 'speech'];

/** 技能大类 → 能力维度（仅用于「根据技能预填长板」这类界面提示，不参与计分） */
export const CATEGORY_TO_ABILITY: Record<SkillCategory, AbilityDimension> = {
  dev: 'tech',
  design: 'design',
  business: 'business',
  data: 'data',
  content: 'content',
  speech: 'speech',
  domain: 'business',
};

/** 队伍角色在邀请表单里的展示顺序 */
export const ROLE_ORDER: TeamRole[] = ['leader', 'tech', 'design', 'business', 'content', 'member'];

/** 看板三列的固定顺序 */
export const TASK_STATUS_ORDER: TaskStatus[] = ['todo', 'doing', 'done'];

/** 下一状态（看板点卡片推进用；纯界面顺序，不是算法） */
export const NEXT_TASK_STATUS: Record<TaskStatus, TaskStatus> = {
  todo: 'doing',
  doing: 'done',
  done: 'todo',
};

/** 阶段顺序（Team 页按阶段分组待办） */
export const PHASE_ORDER: TaskPhase[] = [1, 2, 3, 4];

/** 技能熟练度（shared 的 SkillLevel：1 入门 / 2 熟练 / 3 能带人） */
export const SKILL_LEVEL_LABELS: Record<SkillLevel, string> = {
  1: '入门',
  2: '熟练',
  3: '能带人',
};

/** 账号角色（后端 UserDto.role；shared 未提供标签） */
export const USER_ROLE_LABELS: Record<'student' | 'organizer' | 'admin', string> = {
  student: '学生',
  organizer: '赛事方',
  admin: '管理员',
};

export function userRoleLabel(role: string | undefined): string {
  if (!role) return USER_ROLE_LABELS.student;
  return USER_ROLE_LABELS[role as 'student' | 'organizer' | 'admin'] ?? role;
}

/** 里程碑类型（后端 event_milestones.kind；shared 未提供标签） */
export const MILESTONE_KIND_LABELS: Record<string, string> = {
  register_close: '报名截止',
  prelim: '初赛',
  semi: '复赛',
  final: '决赛',
  custom: '自定义',
};

export function milestoneKindLabel(kind: string | undefined): string {
  if (!kind) return '自定义';
  return MILESTONE_KIND_LABELS[kind] ?? kind;
}

/** 已接入信源主机 → 友好名称（RSS_SOURCES 配置的信源；仅展示用） */
const FEED_SOURCE_NAME_BY_HOST: Record<string, string> = {
  'www.tiaozhanbei.net': '挑战杯官网',
  'www.sues.edu.cn': '工程大官网',
  'youth.sues.edu.cn': '工程大团委',
  'jwc.sues.edu.cn': '工程大教务处',
  'www.lixin.edu.cn': '立信官网',
  'jwc.lixin.edu.cn': '立信教务处',
  'www.suibe.edu.cn': '贸大官网',
  'jwc.suibe.edu.cn': '贸大教务处',
  'tx.suibe.edu.cn': '贸大团委',
  'www.dhu.edu.cn': '东华官网',
  'www.shisu.edu.cn': '上外官网',
  'www.ecupl.edu.cn': '华政官网',
  'www.siva.edu.cn': '视觉官网',
};

/**
 * 动态来源标签：源串形如 `web:https://jwc.suibe.edu.cn/` 或 `rss:<url>`。
 * 能识别主机时显示友好名（如「贸大教务处」），否则回退为主机名或原串。
 */
export function feedSourceLabel(source: string | undefined): string {
  if (!source) return '未知来源';
  const withoutKind = source.replace(/^(web|rss):/, '');
  try {
    const host = new URL(withoutKind).hostname;
    return FEED_SOURCE_NAME_BY_HOST[host] ?? host;
  } catch {
    return withoutKind.length > 0 ? withoutKind : source;
  }
}